import React, { useMemo } from "react";
import { connect } from "react-redux";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import {
    Column,
    DataGrid,
    FilterRow,
    Sorting,
    Paging,
    Pager,
    Export,
    ColumnChooser,
    ColumnChooserSearch,
    Lookup,
} from "devextreme-react/data-grid";
import data from "../../../../assets/apiRoutes.json";
import { EMPTY_GUID, GRID_PAGE_SIZES, CURRENCY_FORMAT } from '../constants';
import SelectCustomerPlaceholder from './SelectCustomerPlaceholder';
import { CollectionCreditTypeOptions, CLOSED_STATUS } from '../Layout';

// Legacy ARD0009's "Collections History" tab (grdBottomHistory) is backed by
// uARM0009.dfm's qryARCollectionCreditHistory:
//
//   ARCOLLECTIONCREDITHEADER
//     left outer join ARCOLLECTIONCREDITDetail on header.ROWID = detail.R_COLLECTIONHEADER
//     left outer join IDMaster on detail.R_CONTACTWITH = IDMaster.ROWID
//   where header.R_CUSTOMER = :paramR_Customer and header.STATUS = 'C'
//
// i.e. only CLOSED follow-ups, flattened one row per detail, showing the detail's contact
// date / promised amount / promised date and the party that was contacted. That is exactly
// what ar/CollectionCreditHeader/GetCollectionCreditHistory returns (the same endpoint OM's
// Query Orders By Customer Credit History tab uses), so it is reused here with Status='C'
// rather than going through id/Customer/GetCollection - which returns every status and no
// detail columns at all.
function HistoryTab(props) {
    const accessToken = props?.oidc?.user?.access_token;
    const hasCustomer = !!props.customerId && props.customerId !== EMPTY_GUID;

    const historyDataSource = useMemo(() => {
        if (!hasCustomer) return null;
        return new DataSource({
            store: createStore({
                // A header can fan out into several detail rows (and a header with no
                // details still returns one row), so the server supplies GridKey - the
                // detail rowid, falling back to the header rowid.
                key: 'GridKey',
                loadParams: { CustomerRowId: props.customerId, Status: CLOSED_STATUS },
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.CollectionCreditHeader.GetCollectionCreditHistory}`,
                onBeforeSend: (method, ajaxOptions) => {
                    ajaxOptions.headers = { Authorization: `Bearer ${accessToken}`, access_token: accessToken };
                },
            }),
            paginate: true,
            pageSize: 100
        });
    }, [accessToken, hasCustomer, props.customerId]);

    if (!hasCustomer) {
        return <SelectCustomerPlaceholder content="collections history" />;
    }

    return (
        <DataGrid
            dataSource={historyDataSource}
            showBorders={true}
            allowColumnReordering={true}
            allowColumnResizing={true}
            hoverStateEnabled={true}
            remoteOperations={true}
            searchEnabled={true}
            columnAutoWidth={true}
            height="100%"
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={GRID_PAGE_SIZES} showNavigationButtons={true} />
            <FilterRow visible={true} />

            {/* Column set mirrors grdBottomHistory. */}
            <Column dataField="RecordNumber" caption="Record #" dataType="string" />
            <Column dataField="Reference" caption="Reference #" dataType="string" />
            <Column dataField="Type" caption="Type" dataType="string">
                <Lookup dataSource={CollectionCreditTypeOptions} displayExpr="text" valueExpr="id" />
            </Column>
            <Column dataField="InitialCallDate" caption="Call Date" dataType="datetime" />
            <Column dataField="ContactDateTime" caption="Contact Date" dataType="datetime" />
            <Column dataField="IdentityId" caption="Contact With" dataType="string" />
            <Column dataField="Name" caption="Name" dataType="string" />
            <Column dataField="PromisedAmount" caption="Promised Amount" dataType="number" format={CURRENCY_FORMAT} />
            <Column dataField="PromisedDate" caption="Promised Date" dataType="datetime" />

            {/* Column chooser (hidden by default). Status is fixed at Closed by the query
                itself, so it is shown only as context rather than as a filterable column. */}
            <Column dataField="Description" caption="Description" dataType="string" visible={false} />
            <Column dataField="NextFollowUpDate" caption="Next Follow-up Date" dataType="datetime" visible={false} />
            <Column dataField="StatusDescription" caption="Status" dataType="string" visible={false} />
            <Column dataField="Comment" caption="Comment" dataType="string" visible={false} />
            <Column dataField="LastUpdateDateTime" caption="Last Updated" dataType="datetime" visible={false} />
            <Column dataField="LastUpdateUserId" caption="Last Updated By" dataType="string" visible={false} />

            <Column dataField="GridKey" caption="Grid Key" visible={false} showInColumnChooser={false} />
            <Column dataField="RowId" caption="Row Id" visible={false} showInColumnChooser={false} />
            <Column dataField="DetailRowId" caption="Detail Row Id" visible={false} showInColumnChooser={false} />
            <Column dataField="Status" caption="Status Flag" visible={false} showInColumnChooser={false} />
        </DataGrid>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(HistoryTab);
